import * as SliderPrimitive from '@radix-ui/react-slider';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { cn } from '@/utils/cn-tw-merge';

interface SliderProps {
  value: number;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  ariaLabel?: string;
  onChange: (newValue: number) => void;
}

export function Slider({
  value,
  unit,
  min = 1,
  max = 200,
  step = 1,
  disabled = false,
  ariaLabel = 'Slider',
  onChange,
}: SliderProps) {
  const accentColor = disabled ? 'bg-gray-400' : 'bg-green-500';
  const tooltipSurface = disabled ? 'bg-gray-100' : 'bg-green-100';
  const tooltipText = disabled ? 'text-gray-400' : 'text-green-500';

  return (
    <SliderPrimitive.Root
      className="relative flex h-6 w-full items-center data-[disabled]:cursor-not-allowed"
      value={[value]}
      onValueChange={(newValue) => onChange(newValue[0])}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      data-testid={DataTestIdEnum.SLIDER_ROOT}
    >
      <SliderPrimitive.Track
        className="relative h-1 w-full grow rounded-full bg-gray-200"
        data-testid={DataTestIdEnum.SLIDER_TRACK}
      >
        <SliderPrimitive.Range
          className={cn('absolute h-full rounded-full', accentColor)}
          data-testid={DataTestIdEnum.SLIDER_RANGE}
        />
      </SliderPrimitive.Track>

      <SliderPrimitive.Thumb
        className={cn(
          'flex size-6 cursor-pointer items-center justify-center rounded-full outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2 data-[disabled]:cursor-not-allowed',
          accentColor,
        )}
        aria-label={ariaLabel}
        aria-valuetext={`${value}${unit ?? ''}`}
        data-testid={DataTestIdEnum.SLIDER_THUMB}
      >
        <span className="size-3 rounded-full border-[1.2px] border-white" />

        <span
          aria-hidden
          className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 flex -translate-x-1/2 flex-col items-center"
        >
          <span
            className={cn(
              'whitespace-nowrap rounded-lg px-3 py-2 text-base font-semibold leading-6 shadow-tooltip',
              tooltipSurface,
              tooltipText,
            )}
            data-testid={DataTestIdEnum.SLIDER_TOOLTIP}
          >
            {value}
            {unit}
          </span>
          <span className={cn('-mt-1 size-2.5 rotate-45 rounded-[2px]', tooltipSurface)} />
        </span>
      </SliderPrimitive.Thumb>
    </SliderPrimitive.Root>
  );
}
